import { ResourceCapacitiesResponseDto } from './capacity.models';
import { PagedResult } from './common.models';
import { NotesWithMentionsDto } from './notes.models';

/**
 * Raw employee/directory record (mirrors the API's EmployeeDto), sourced from
 * the same employee data as ResourceDto. Used where a person needs to be shown
 * by their directory details (e.g. a matter's billing partner / supervisor).
 */
export interface EmployeeDto {
  id: string;
  lastName: string;
  firstName: string;
  departmentCode: string;
  title: string;
  officeCode: string;
  groupNum: string;
  extension: string;
  emailAlias: string;
  floor: string;
  active: boolean;
  employmentStatus: string;
  classYear: string;
  hireDate?: string;
  terminationDate?: string;
  officeLocation: string;
}

export interface ResourceDto {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  employeeId: string;
  email: string;
  phoneNumber: string;
  title: string;
  /** Free text, not a number — the API serializes ClassYear as a string, and the sync stores it
   *  verbatim from the external employee API, so values like "2020 LLM" are possible. */
  classYear: string;
  office: string;
  seniority?: string;
  seniorityCode?: string;
  /** Seniority rank from the server lookup (lower sorts first); used for client-side sorting. */
  senioritySortOrder?: number;
  practiceAreas: PracticeAreaDto[];
  /** Profile tags (Skills, Jurisdictions, Languages, Interests, Sub-Practice Areas). */
  tags?: ResourceTagDto[];
  assignments: ResourceAssignmentDto[];
  resourceCapacities?: ResourceCapacitiesResponseDto;
  billableHours?: number;
  nonBillableHours?: number;
  proBonoHours?: number;
  businessDevelopmentHours?: number;
  absenceHours?: number;
  firmHours?: number;
  totalHours?: number;
}

/**
 * One office a resource belongs to. Mirrors the API's OfficeMembershipDto. `isPrimary` is true for
 * the primary office (employee sync record), false for a secondary association.
 * `secondaryAssociationId` is the removable association id for a secondary membership; null/absent
 * for a primary office (primary wins).
 */
export interface OfficeMembershipDto {
  officeCode: string;
  officeName: string;
  isPrimary: boolean;
  secondaryAssociationId?: number | null;
}

/**
 * A resource returned by the office-resources endpoint, carrying its office memberships as a
 * collection. Mirrors the API's OfficeResourceDto. Modeled as a list so one row can represent
 * primary + secondary offices; the office admin list populates exactly one membership (the
 * requested office) per row.
 */
export interface OfficeResourceDto extends ResourceDto {
  officeMemberships: OfficeMembershipDto[];
}

export interface ResourceDetailDto extends ResourceDto {
  // Additional fields for the detail view
  // Note: avatarUrl and room will be computed/generated since they're not in the database
  avatarUrl?: string;
  room?: string;
  notesInfo?: NotesWithMentionsDto; // Notes info including content and last edit history
}

export interface ActiveMatterDto {
  id: string; // This will be clientNumber-matterNumber (e.g., "C001-M2024")
  name: string; // Matter title
  clientName: string; // Client name for concatenation
  practiceArea: string; // Primary practice area name
  region: string; // Matter region
  responsiblePartner: string; // Matter billing partner
}

export interface PracticeAreaDto {
  practiceCode: string;
  description: string;
  // Source of the ASSIGNMENT of an employee to this practice area (EmployeePracticeAreaSource);
  // only set when returned as part of a ResourceDto from admin context. EmployeeSync (2) marks the
  // assignment as the employee's primary and therefore non-removable.
  //
  // Named for the assignment because this DTO could plausibly carry either source: PracticeAreaSource
  // (how the practice area itself was created) is numbered to parallel this enum — 2 means Sync there
  // and EmployeeSync here — so a mix-up would type-check and serialise while meaning something else.
  // Row origin is therefore carried as the boolean `isCustom` below, never as a second numeric
  // `source`.
  assignmentSource?: number;
  // True when the practice area was created in this app rather than synced from the firm
  // directory, set by the API from the stored record. A custom area is never a resource's primary.
  isCustom?: boolean;
}

export enum EmployeePracticeAreaSource {
  Manual = 1,
  EmployeeSync = 2,
}

export interface EmployeePracticeAreaDto {
  practiceAreaCode: string;
  practiceAreaName: string;
  assignedAt: string; // ISO date string from API
  source: number; // EmployeePracticeAreaSource
}

export interface OfficeDetailDto {
  code: string;
  description: string;
  active: boolean;
  letterCode: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  telephone: string | null;
  fax: string | null;
  resourceCount: number;
}

/**
 * Mirrors the API's EmployeeSecondaryOfficeDto. Represents an association
 * between an employee and an additional (secondary) office.
 */
export interface EmployeeSecondaryOfficeDto {
  id: number;
  employeeId: string;
  officeCode: string;
  createdBy: number;
  createdAt: string;
  updatedBy?: number | null;
  updatedAt?: string | null;
}

export interface ResourceAssignmentDto {
  id: number;
  resourceId: number;
  matterId: number;
  createdAt: string;
  updatedAt?: string;
  isActive: boolean;
  isDeleted: boolean;

  // Client and Matter details for active matters display
  clientNumber: string;
  clientName: string;
  matterNumber: string;
  matterTitle: string;
  matterRegion: string;
  matterBillingPartner: string;
  primaryPracticeArea: string;
}

export interface ResourceListRequest {
  page: number;
  pageSize: number;
  search?: string;
  titles?: string[];
  title?: string;
  classYears?: string[];
  offices?: string[];
  office?: string;
  practiceAreaCodes?: string[];
  practiceAreaCode?: string;
  capacityStatusCodes?: string[];
  seniorityCodes?: string[];
  skillCodes?: string[];
  jurisdictionCodes?: string[];
  languageCodes?: string[];
  interestCodes?: string[];
  subPracticeAreaCodes?: string[];
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  excludePracticeAreaCode?: string;
  /** Exclude employees already in this office (primary office or active secondary association). */
  excludeOfficeCode?: string;
}

/** Tag category names as serialized by the API's ResourceTagCategory enum. */
export type ResourceTagCategory =
  | 'Skill'
  | 'Jurisdiction'
  | 'Language'
  | 'Interest'
  | 'SubPractice';

/** A resource tag as returned by the API's tag endpoints. */
export interface ResourceTagDto {
  category: ResourceTagCategory;
  name: string;
  code: string;
  lookupValueId: number;
}

/** A single lookup-backed attribute value on a resource's profile. */
export interface ResourceAttributeValueDto {
  code: string;
  name: string;
  /** Backing lookup value id; passed to the delete endpoints to remove the tag. */
  lookupValueId: number;
}

/** All profile attribute associations for a resource, grouped by category. */
export interface ResourceAttributesDto {
  skills: ResourceAttributeValueDto[];
  jurisdictions: ResourceAttributeValueDto[];
  languages: ResourceAttributeValueDto[];
  interests: ResourceAttributeValueDto[];
  subPracticeAreas: ResourceAttributeValueDto[];
}

export interface ResourceTimeOffRequest extends ResourceListRequest {
  startDate?: string;
  endDate?: string;
  statusCodes?: string[];
  /** PAST/CURRENT/FUTURE relative to referenceDate; omitted = all */
  timeFrames?: string[];
  /** The user's local "today" (yyyy-MM-dd) used by the timeFrames filter */
  referenceDate?: string;
}

/** Workbook layout for the resource time off export. Matches the API enum
 *  `ResourceTimeOffExportLayout`. */
export type ResourceTimeOffExportLayout = 'grouped' | 'flattened';

export interface ResourceTimeOffEntryDto {
  employeeId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  timeOffRequestId: number;
  typeName: string;
  typeCode: string;
  statusName: string;
  statusCode: string;
  startDate?: string;
  endDate?: string;
  daysOff: number;
  days: string[];
  /** Subset of `days` that drew from vacation carry-over (per-day precise). PBI 5770 AC#3. */
  carryOverDays?: string[];
  practiceAreas: PracticeAreaDto[];
  officeName?: string;
  classYear?: string;
  seniorityCode?: string;
  seniority?: string;
  hasNotes?: boolean;
  hasManagerNotes?: boolean;
}

export interface ResourceTimeOffResultDto {
  totalEmployees: number;
  timeOffEntries: ResourceTimeOffEntryDto[];
}

export type { PagedResult };
